import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Inspection } from '@shared/types/index.ts';
import type { InspectionItemInput } from '../api/endpoints.ts';
import { offlinePrivacyResetIsCurrent } from './offline-privacy-reset.ts';

/**
 * Durable, deliberately small working copies for quality inspections.
 *
 * Unlike the read-through API cache, every row in this database represents
 * work the server has not acknowledged. Rows are therefore never aged out or
 * evicted to make room: reaching a bound refuses the new write and leaves all
 * existing work intact. A successful PATCH (or an explicit sign-out privacy
 * wipe) is the only thing that removes a draft.
 */

const DB_NAME = 'blasto-inspection-drafts';
const DB_VERSION = 1;
const STORE = 'drafts';

export const INSPECTION_DRAFT_LIMITS = {
  maxEntries: 15,
  maxEntryBytes: 512 * 1024,
  maxTotalBytes: 3 * 1024 * 1024,
} as const;

export interface InspectionDraftScope {
  userId: string;
  companyId: string;
  locationId: string | null;
  /** Only the authorization facts that control access to inspection drafts. */
  authorization: string;
}

export type InspectionDraftQueue = 'all' | 'notes' | null;

export interface InspectionDraft {
  key: string;
  scope: string;
  inspectionId: string;
  /** The server copy that was on screen when this run of local work began. */
  inspection: Inspection;
  /** Exact canonical server state, used because inspections have no version. */
  baseFingerprint: string;
  items: InspectionItemInput[];
  internalNotes: string;
  customerNotes: string;
  linesDirty: boolean;
  notesDirty: boolean;
  /** Non-null only after the user asked for this portion to be sent. */
  queued: InspectionDraftQueue;
  /** A recovered or ambiguously failed write must GET fresh before PATCHing. */
  requiresPreflight: boolean;
  /** Cross-tab-unique editor activation that owns this durable variant. */
  editorOwnerId?: string;
  /** Monotonic only within editorOwnerId. */
  revision: number;
  updatedAt: number;
  sizeBytes: number;
}

export type InspectionDraftToSave = Omit<InspectionDraft, 'key' | 'scope' | 'updatedAt' | 'sizeBytes'>;
export type InspectionDraftSaveResult = 'saved' | 'inactive' | 'entry-too-large' | 'capacity-reached';

export interface InspectionDraftAcknowledgment {
  /** Current releases acknowledge by owner; legacy recoveries use their exact key. */
  editorOwnerId?: string;
  key?: string;
  throughRevision: number;
}

interface InspectionDraftSchema extends DBSchema {
  [STORE]: {
    key: string;
    value: InspectionDraft;
    indexes: { 'by-scope': string; 'by-updated': number };
  };
}

let dbPromise: Promise<IDBPDatabase<InspectionDraftSchema>> | null = null;
let activeScope: string | null = null;
let scopeGeneration = 0;
/** Invalidates explicit old-scope flushes across sign-out/impersonation. */
let writeFenceGeneration = 0;
let writesSuspended = false;

function database(): Promise<IDBPDatabase<InspectionDraftSchema>> {
  if (dbPromise) return dbPromise;
  const opening = openDB<InspectionDraftSchema>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      const store = db.createObjectStore(STORE, { keyPath: 'key' });
      store.createIndex('by-scope', 'scope');
      store.createIndex('by-updated', 'updatedAt');
    },
  });
  dbPromise = opening;
  void opening.catch(() => {
    if (dbPromise === opening) dbPromise = null;
  });
  return opening;
}

async function safely<T>(operation: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await operation();
  } catch {
    return fallback;
  }
}

function encodedScope(scope: InspectionDraftScope): string {
  return JSON.stringify([scope.userId, scope.companyId, scope.locationId, scope.authorization]);
}

export function setInspectionDraftScope(scope: InspectionDraftScope | null): void {
  const next = scope ? encodedScope(scope) : null;
  if (next === activeScope) return;
  activeScope = next;
  scopeGeneration += 1;
  if (next === null) writeFenceGeneration += 1;
}

export function suspendInspectionDraftWrites(): void {
  writesSuspended = true;
  scopeGeneration += 1;
  writeFenceGeneration += 1;
}

export function resumeInspectionDraftWrites(): void {
  writesSuspended = false;
}

function canonicalItem(item: InspectionItemInput) {
  return {
    id: item.id ?? null,
    areaName: item.areaName,
    itemName: item.itemName,
    rating: item.rating ?? null,
    comment: item.comment || null,
    photoFileId: item.photoFileId ?? null,
  };
}

/**
 * The fields this editor can overwrite, in their declarative order.
 *
 * This intentionally excludes sharing and corrective actions: a colleague
 * raising an action should not conflict with a score save that cannot touch
 * that action. Status is included because a completed/canceled walkthrough
 * must never receive a replay meant for an editable one.
 */
export function inspectionDraftFingerprint(
  inspection: Pick<Inspection, 'status' | 'internalNotes' | 'customerNotes' | 'items'>,
): string {
  return JSON.stringify({
    status: inspection.status,
    internalNotes: inspection.internalNotes || null,
    customerNotes: inspection.customerNotes || null,
    items: inspection.items.map(canonicalItem),
  });
}

/**
 * Advances only the fields a notes-only save acknowledged while dirty scored
 * lines remain. The response can contain newer lines from another supervisor;
 * using those as this device's base would hide the conflict on its next save.
 */
export function inspectionBaseAfterNotesSave(previousLineBase: Inspection, acknowledged: Inspection): Inspection {
  return {
    ...acknowledged,
    status: previousLineBase.status,
    items: previousLineBase.items,
  };
}

function itemMatches(serverItem: Inspection['items'][number], draftItem: InspectionItemInput): boolean {
  if (draftItem.id && serverItem.id !== draftItem.id) return false;
  const server = canonicalItem(serverItem);
  const draft = canonicalItem(draftItem);
  // A newly-created local row has no id; the server assigns one on ACK.
  if (!draftItem.id) {
    server.id = null;
    draft.id = null;
  }
  return JSON.stringify(server) === JSON.stringify(draft);
}

/**
 * Recognizes a PATCH whose response was lost. This makes ambiguous network
 * failure safe: the next fresh GET can count an exact match as the missing ACK
 * instead of sending a second declarative replacement.
 */
export function inspectionMatchesDraft(
  server: Inspection,
  draft: Pick<InspectionDraft, 'items' | 'internalNotes' | 'customerNotes'>,
  queue: Exclude<InspectionDraftQueue, null>,
): boolean {
  if ((server.internalNotes ?? '') !== draft.internalNotes || (server.customerNotes ?? '') !== draft.customerNotes) {
    return false;
  }
  if (queue === 'notes') return true;
  return (
    server.items.length === draft.items.length &&
    server.items.every((item, index) => itemMatches(item, draft.items[index]!))
  );
}

function measuredBytes(value: Omit<InspectionDraft, 'sizeBytes'> | InspectionDraft): number {
  return new TextEncoder().encode(JSON.stringify({ ...value, sizeBytes: undefined })).byteLength + 64;
}

export function inspectionDraftBudgetStatus(
  existing: readonly InspectionDraft[],
  candidate: Omit<InspectionDraft, 'sizeBytes'> | InspectionDraft,
): Exclude<InspectionDraftSaveResult, 'inactive'> {
  const candidateBytes = measuredBytes(candidate);
  if (candidateBytes > INSPECTION_DRAFT_LIMITS.maxEntryBytes) return 'entry-too-large';

  const others = existing.filter((draft) => draft.key !== candidate.key);
  if (others.length + 1 > INSPECTION_DRAFT_LIMITS.maxEntries) return 'capacity-reached';
  const totalBytes = others.reduce((total, draft) => total + measuredBytes(draft), 0) + candidateBytes;
  return totalBytes > INSPECTION_DRAFT_LIMITS.maxTotalBytes ? 'capacity-reached' : 'saved';
}

export async function saveInspectionDraft(
  input: InspectionDraftToSave,
  scopeOverride?: InspectionDraftScope,
): Promise<InspectionDraftSaveResult> {
  if (!activeScope || writesSuspended || !offlinePrivacyResetIsCurrent()) return 'inactive';
  // A page leaving one location is allowed to finish exactly one local flush
  // into that old location's key. It must never derive the key from the newly
  // active scope. Sign-out/impersonation still fences the write through the
  // independent write generation and the non-null active-scope requirement.
  const scope = scopeOverride ? encodedScope(scopeOverride) : activeScope;
  const generation = scopeGeneration;
  const writeGeneration = writeFenceGeneration;
  const key = JSON.stringify([scope, input.inspectionId, input.editorOwnerId ?? 'legacy']);

  return safely(async () => {
    const db = await database();
    if (
      writesSuspended ||
      !activeScope ||
      !offlinePrivacyResetIsCurrent() ||
      writeGeneration !== writeFenceGeneration ||
      (!scopeOverride && (activeScope !== scope || generation !== scopeGeneration))
    ) {
      return 'inactive';
    }

    const transaction = db.transaction(STORE, 'readwrite');
    const current = await transaction.store.get(key);
    // An old debounce that arrived after a newer edit is harmless.
    if (current && current.revision > input.revision) {
      await transaction.done;
      return 'saved';
    }

    const withoutSize = { ...input, key, scope, updatedAt: Date.now() };
    const record: InspectionDraft = {
      ...withoutSize,
      sizeBytes: measuredBytes(withoutSize),
    };
    const all = await transaction.store.getAll();
    const budget = inspectionDraftBudgetStatus(all, record);
    if (budget !== 'saved') {
      await transaction.done;
      return budget;
    }

    if (
      writesSuspended ||
      !activeScope ||
      !offlinePrivacyResetIsCurrent() ||
      writeGeneration !== writeFenceGeneration ||
      (!scopeOverride && (activeScope !== scope || generation !== scopeGeneration))
    ) {
      await transaction.done;
      return 'inactive';
    }
    await transaction.store.put(record);
    await transaction.done;
    return 'saved';
  }, 'inactive');
}

export async function loadInspectionDraft(
  inspectionId: string,
  scopeOverride?: InspectionDraftScope,
): Promise<InspectionDraft | null> {
  if (!activeScope || writesSuspended || !offlinePrivacyResetIsCurrent()) return null;
  // The global cache scope includes every permission and plan feature because
  // API responses span the whole product. An inspection draft is narrower: an
  // unrelated permission grant must not strand an unsaved walkthrough.
  const scope = scopeOverride ? encodedScope(scopeOverride) : activeScope;
  const generation = scopeGeneration;
  const writeGeneration = writeFenceGeneration;
  return safely(async () => {
    const db = await database();
    const candidates = (await db.getAllFromIndex(STORE, 'by-scope', scope))
      .filter((draft) => draft.inspectionId === inspectionId)
      .sort(
        (left, right) =>
          right.updatedAt - left.updatedAt || right.revision - left.revision || right.key.localeCompare(left.key),
      );
    if (
      writesSuspended ||
      !activeScope ||
      !offlinePrivacyResetIsCurrent() ||
      writeGeneration !== writeFenceGeneration ||
      (!scopeOverride && (activeScope !== scope || generation !== scopeGeneration))
    ) {
      return null;
    }
    return candidates[0] ?? null;
  }, null);
}

/** Remove only owner revisions that a server write or explicit discard covered. */
export async function clearInspectionDraft(
  inspectionId: string,
  scopeOverride?: InspectionDraftScope,
  acknowledgments: readonly InspectionDraftAcknowledgment[] = [],
): Promise<void> {
  if (!activeScope || acknowledgments.length === 0) return;
  const scope = scopeOverride ? encodedScope(scopeOverride) : activeScope;
  const generation = scopeGeneration;
  const writeGeneration = writeFenceGeneration;
  await safely(async () => {
    const db = await database();
    if (
      !activeScope ||
      writeGeneration !== writeFenceGeneration ||
      (!scopeOverride && (activeScope !== scope || generation !== scopeGeneration))
    ) {
      return;
    }
    const transaction = db.transaction(STORE, 'readwrite');
    const candidates = (await transaction.store.index('by-scope').getAll(scope)).filter(
      (draft) => draft.inspectionId === inspectionId,
    );
    for (const draft of candidates) {
      const covered = acknowledgments.some(
        (acknowledgment) =>
          draft.revision <= acknowledgment.throughRevision &&
          ((acknowledgment.editorOwnerId && draft.editorOwnerId === acknowledgment.editorOwnerId) ||
            (acknowledgment.key && draft.key === acknowledgment.key)),
      );
      if (covered) await transaction.store.delete(draft.key);
    }
    await transaction.done;
  }, undefined);
}

/** Privacy wipe for explicit/forced sign-out. */
export async function clearAllInspectionDrafts(): Promise<boolean> {
  setInspectionDraftScope(null);
  return safely(async () => {
    await (await database()).clear(STORE);
    return true;
  }, false);
}
